import React, { Component } from "react";
import { withTranslation } from "react-i18next";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import WithSelect from "@components/select";
import * as uploadActions from "@modules/upload";
import * as uploadQueueActions from "@modules/uploadQueue";
import * as sourcesActions from "@modules/sources";
import * as notificationActions from "@modules/notification";
import UploadQueueList from "@routes/sources/components/UploadQueueList";
import {
    UPLOAD_FILE_LOCATION_TYPE,
    CONSTANTS,
    UPLDOAD_DUPLICATE_ACTION,
    OPERATING_TYPE,
    UPLOAD_DUPLICATE_MODAL_ACTION,
    UPLOAD_STATUS,
} from "@constants";
import getFilesInS3 from "@cores/getFilesInS3";
import DuplicateCheckModal from "@components/modals/DuplicateCheckModal";
import equal from "deep-equal";

const duplicateModalRef = React.createRef();
const locationType = UPLOAD_FILE_LOCATION_TYPE.LOCAL || "Local";

class UploadFilesPanel extends Component {
    constructor(props) {
        super(props);
        this.state = {
            confirmModalIsOpened: false,
        };
    }

    componentDidMount = async () => {
        this.checkAndUploadStart();
    };

    shouldComponentUpdate = (nextProps, nextState) => {
        if (this.props.uploadQueue !== nextProps.uploadQueue) {
            return true;
        }
        if (this.state !== nextState) {
            return true;
        }
        return false;
    };

    componentDidUpdate = async (prevProps, prevState) => {
        const { uploadQueue, UploadQueueActions } = this.props;
        if (!equal(prevProps.uploadQueue, uploadQueue)) {
            this.checkAndUploadStart();
        }
    };

    checkAndUploadStart = () => {
        const { uploadQueue, UploadQueueActions } = this.props;
        uploadQueue[locationType].length > 0 &&
            uploadQueue[locationType].map(async (item) => {
                if (item.status === UPLOAD_STATUS.UPLOADING && !item.isStarted) {
                    this.startUpload(item.key);
                    await UploadQueueActions.updateQueue(locationType, item.key, {
                        isStarted: true,
                    });
                }
                return item;
            });
    };

    startUpload = (key) => {
        this.retryUpload(key);
    };

    retryUpload = async (key, uploadDuplicateAction = UPLDOAD_DUPLICATE_ACTION.ASK) => {
        const { uploadQueue, UploadQueueActions, sources, NotificationActions } = this.props;
        const hierarchy = sources.hierarchy;
        const hierarchyActive = "folder";
        const locationType = UPLOAD_FILE_LOCATION_TYPE.LOCAL || "Local";
        const { retryKey } = await UploadQueueActions.retryUploadQueueJob({ locationType, key });
        const results = await this.checkAndUploadFiles(locationType, retryKey, uploadDuplicateAction);
        // console.log('results', results)

        if (results === "SUCCEED" || results === "COMPLETE") {
            const checkTarget =
                uploadQueue[locationType]
                    .find((item) => item.key === retryKey)
                    .files.filter((item) => item.status === "ERROR") || [];
            const partialComplete = checkTarget.length > 0;
            UploadQueueActions.uploadFilesFinished({ locationType, key: retryKey, status: UPLOAD_STATUS.COMPLETE });
            this.toastJustMessage("success", `${partialComplete ? `Partial ` : ``}Upload completed.`);
        } else if (results === "CANCELED") {
            UploadQueueActions.uploadFilesFinished({ locationType, key: retryKey, status: UPLOAD_STATUS.CANCELED });
            this.toastJustMessage("success", `Upload canceled.`);
        } else if (results === "ERROR") {
            UploadQueueActions.uploadFilesFinished({ locationType, key: retryKey, status: UPLOAD_STATUS.ERROR });
            this.toastJustMessage("error", `Upload error.`);
        }

        setTimeout(() => {
            const stillHere = equal(
                JSON.stringify(hierarchy[hierarchyActive].selected),
                JSON.stringify(this.props.sources.hierarchy["folder"].selected),
            );
            if (stillHere) {
                this.refreshSourceView();
            }
        }, 1000);
    };

    toastJustMessage = (type, message) => {
        const { NotificationActions } = this.props;
        NotificationActions.setNotification({
            type,
            contents: `${message}`,
        });
    };

    refreshSourceView = async (time) => {
        time = time || 500;
        const { SourcesActions } = this.props;
        await SourcesActions.sourceViewReload(true);
        setTimeout(() => {
            SourcesActions.sourceViewReload(false);
        }, time);
    };

    pollingForConfirmModalCheck = async () => {
        const doSomething = () => {
            return new Promise((resolve) =>
                setTimeout(async () => {
                    try {
                        if (this.state.confirmModalIsOpened === true) {
                            resolve("OPENED");
                        } else {
                            resolve("CLOSED");
                        }
                    } catch (error) {
                        resolve("ERROR");
                    }
                }, 1000),
            );
        };

        const loop = () => {
            return doSomething().then((results) => {
                if (results === "CLOSED" || results === "ERROR") {
                    return results;
                } else {
                    return loop();
                }
            });
        };

        return new Promise((resolve, reject) => {
            loop()
                .then((results) => {
                    resolve(results);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    };

    onClickCreateNewFolder = (folderFullPath, folderName, jobKey) => {
        const { uploadQueue, SourcesActions } = this.props;
        const targets = uploadQueue[locationType].filter((item) => item.key === jobKey)[0];
        const bucketPath = targets.bucketPath;
        const bucketName = bucketPath.bucket;
        const folderPath = bucketPath.folderPath ? `/${bucketPath.folderPath}` : "";

        let makePath = `${folderPath}/${
            folderFullPath === "/"
                ? folderName
                : folderFullPath[0] === "/"
                ? `${folderFullPath.slice(1)}`
                : `${folderFullPath}`
        }`;
        makePath = makePath.slice(-folderName.length) !== folderName ? makePath + folderName : makePath;

        const items = [
            {
                path: makePath,
                isFolder: true,
            },
        ];
        return new Promise(async (resolve, reject) => {
            try {
                const response = await SourcesActions.createFolder(bucketName, items);
                // console.log('response', response)
                resolve("COMPLETE");
            } catch (error) {
                resolve("ERROR");
            }
        });
    };

    checkAndUploadFiles = async (locationType, jobKey, uploadDuplicateAction) => {
        const { uploadQueue, UploadQueueActions } = this.props;
        const targets = uploadQueue[locationType].filter((item) => item.key === jobKey)[0];
        const selectedFiles = targets.files;
        const bucketPath = targets.bucketPath;
        const isFolder = targets.isFolder;

        const filePut = async (selectedFile) => {
            if (selectedFile.isFolder) {
                try {
                    await this.onClickCreateNewFolder(selectedFile.fullPath, selectedFile.name, jobKey);
                    return await fileAlreadyUploaded(selectedFile);
                } catch (error) {
                    return await fileError(selectedFile);
                }
            } else {
                // console.log(`33333333333333333333333`);

                return await UploadQueueActions.uploadJobFiles({
                    locationType,
                    id: selectedFile.id,
                    key: jobKey,
                    uploadCompleteCallback: () => {},
                });
            }
        };

        const fileCancel = async (selectedFile) => {
            return await UploadQueueActions.uploadJobFileSetStatus({
                locationType,
                id: selectedFile.id,
                key: jobKey,
                status: "CANCELED",
                uploadCompleteCallback: () => {},
            });
        };

        const fileSkip = async (selectedFile) => {
            return await UploadQueueActions.uploadJobFileSetStatus({
                locationType,
                id: selectedFile.id,
                key: jobKey,
                status: "CANCELED",
                uploadCompleteCallback: () => {},
            });
        };

        const fileError = async (selectedFile) => {
            return await UploadQueueActions.uploadJobFileSetStatus({
                locationType,
                id: selectedFile.id,
                key: jobKey,
                status: "ERROR",
                uploadCompleteCallback: () => {},
            });
        };

        const fileAlreadyUploaded = async (selectedFile) => {
            return await UploadQueueActions.uploadJobFileSetStatus({
                locationType,
                id: selectedFile.id,
                key: jobKey,
                status: "COMPLETE",
                uploadCompleteCallback: () => {},
            });
        };

        return new Promise(async (resolve, reject) => {
            let isAllSkip = uploadDuplicateAction === UPLDOAD_DUPLICATE_ACTION.ALWAYS_SKIP;
            let isAllOverwrite = uploadDuplicateAction === UPLDOAD_DUPLICATE_ACTION.ALWAYS_OVERWRITE;
            const s3UrlBase = `https://${bucketPath.bucket}.s3.amazonaws.com${
                bucketPath.folderPath !== "" ? "/" + bucketPath.folderPath : ""
            }`;
            // const temp = await modal.show(selectedFiles);
            let status = "SUCCEED";
            let errorOccured = false;

            for await (const selectedFile of selectedFiles) {
                // isAllOverwrite = ture 라면 s3 에 중복파일 있는지 체크 할 필요 없이 모두 넣고 끝
                if (selectedFile.status === "COMPLETE") {
                    fileAlreadyUploaded(selectedFile);
                    continue;
                }

                if (isAllOverwrite) {
                    const filePutResponse = await filePut(selectedFile);
                    status = filePutResponse.status;
                    if (status === "CANCELED") {
                        resolve("CANCELED");
                        return;
                    } else if (status === "ERROR") {
                        errorOccured = true;
                    }
                    continue;
                }

                let checkUrl;
                let filePath = `${selectedFile.fullPath}${selectedFile.name}`;

                checkUrl = `${s3UrlBase}${filePath}`;

                const isExistFileToS3 = await this.checkExisitFileToS3(checkUrl);
                // isAllSkip = ture 라면 s3 에 중복파일 없는 것만 넣고 끝
                if (isAllSkip && !isExistFileToS3) {
                    const filePutResponse = await filePut(selectedFile);
                    status = filePutResponse.status;
                    if (status === "CANCELED") {
                        resolve("CANCELED");
                        return;
                    } else if (status === "ERROR") {
                        errorOccured = true;
                    }
                    continue;
                } else if (isAllSkip && isExistFileToS3) {
                    await fileSkip(selectedFile);
                    continue;
                }

                if (isExistFileToS3 === "EXIST") {
                    const duplicateModal = duplicateModalRef.current;

                    if (this.state.confirmModalIsOpened) {
                        const checkModalStatus = await this.pollingForConfirmModalCheck();
                        // console.log('checkModalStatus', checkModalStatus)
                    }

                    this.setState({
                        ...this.state,
                        confirmModalIsOpened: true,
                    });
                    const result = await duplicateModal.show({
                        operatingType: OPERATING_TYPE.UPLOAD,
                        totalCount: selectedFiles.length,
                        distFolderName:
                            bucketPath.folderPath === ""
                                ? bucketPath.bucket
                                : `${bucketPath.bucket}/${bucketPath.folderPath}`,
                        displayName: selectedFile.name,
                    });
                    this.setState({
                        ...this.state,
                        confirmModalIsOpened: false,
                    });

                    switch (result.action) {
                        case UPLOAD_DUPLICATE_MODAL_ACTION.OVERWRITE:
                            const filePutResponse = await filePut(selectedFile);
                            status = filePutResponse.status;
                            if (status === "CANCELED") {
                                resolve("CANCELED");
                                return;
                            } else if (status === "ERROR") {
                                errorOccured = true;
                                if (selectedFiles.length === 1) {
                                    resolve("ERROR");
                                }
                            }
                            isAllOverwrite = result.isApplyToAll;
                            break;
                        case UPLOAD_DUPLICATE_MODAL_ACTION.SKIP:
                            isAllSkip = result.isApplyToAll;
                            await fileSkip(selectedFile);
                            break;
                        case UPLOAD_DUPLICATE_MODAL_ACTION.STOP:
                            await fileCancel(selectedFile);
                            resolve("CANCELED");
                            return;
                    }
                } else if (isExistFileToS3 === "ERROR") {
                    await fileError(selectedFile);
                    resolve("ERROR");
                    return;
                } else {
                    const filePutResponse = await filePut(selectedFile);
                    status = filePutResponse.status;
                    if (status === "CANCELED") {
                        resolve("CANCELED");
                        return;
                    } else if (status === "ERROR") {
                        errorOccured = true;
                        if (selectedFiles.length === 1) {
                            resolve("ERROR");
                        }
                    }
                }
            }
            resolve("COMPLETE");
        });
    };

    checkExisitFileToS3 = async (checkUrl) => {
        const { stage, project } = this.props;

        try {
            await getFilesInS3(stage.id, stage.endpoint, checkUrl, project.id);
            //status 200 이면 s3에 존재
            // isExistFileToS3 = true;
            return "EXIST";
        } catch (e) {
            // console.log("e: ", e);
            if (e?.response?.status === 404) {
                return false;
            } else {
                return "ERROR";
            }
        }
    };

    cancelUpload = async (key) => {
        const { UploadQueueActions } = this.props;
        const result = await UploadQueueActions.cancelQueue(locationType, key);
        return result;
    };

    onToggleFileList = (key) => {
        const { UploadQueueActions } = this.props;
        UploadQueueActions.toggleQueueExpander(locationType, key);
    };

    onChangeLimit = (offset) => {
        const { onChangeLimit } = this.props;
        onChangeLimit(offset);
    };

    onSubmit = () => {
        const { UploadQueueActions, uploadQueue, local_files_offset } = this.props;
        const offset = uploadQueue.local_files_offset + this.props.local_files_offset;
        UploadQueueActions.changeFilesOffset(offset);
    };

    render = () => {
        const { uploadQueue, local_files_offset, t } = this.props;
        const uploadQueueList = uploadQueue[locationType].slice(0, uploadQueue.local_files_offset);
        const viewmoreEnable = uploadQueue[locationType].length > uploadQueue.local_files_offset;
        return (
            <React.Fragment>
                <div className="panel-body">
                    <DuplicateCheckModal ref={duplicateModalRef} />
                    {uploadQueueList.length > 0 ? (
                        <div className="box-scroll">
                            <UploadQueueList
                                uploadQueueList={uploadQueueList}
                                onCancel={this.cancelUpload}
                                onRetry={this.retryUpload}
                                onToggleFileList={this.onToggleFileList}
                                statusControl={false}
                            />
                        </div>
                    ) : (
                        <React.Fragment>
                            <div className={"empty"}>
                                <i className={"sprite sprite-nojob"}></i>
                                <p>
                                    {t([`common::msg::${"No {{name}} searched."}`, `No {{name}} searched.`], {
                                        name: t([`common::label::${"Upload Files"}`, `Upload Files`]),
                                    })}
                                </p>
                            </div>
                        </React.Fragment>
                    )}
                </div>
                {uploadQueueList.length > 0 && (
                    <div className="panel-footer">
                        <WithSelect
                            defaultValue={local_files_offset}
                            options={CONSTANTS("LIST_LIMITS")}
                            onChange={(option) => this.onChangeLimit(option.value)}
                        />
                        {viewmoreEnable ? (
                            <button
                                type={"button"}
                                className={"btn btn-outline-default"}
                                onClick={(e) => {
                                    e.preventDefault();
                                    this.onSubmit();
                                }}
                            >
                                <span>{`View More`}</span>
                                <i className="sprite sprite-angle-down"></i>
                            </button>
                        ) : (
                            ""
                        )}
                    </div>
                )}
            </React.Fragment>
        );
    };
}

let mapStateToProps = (state) => {
    return {
        stage: state.stage,
        project: state.project,
        uploadQueue: state.uploadQueue,
        sources: state.sources,
    };
};

let mapDispatchToProps = (dispatch) => {
    return {
        UploadActions: bindActionCreators(uploadActions, dispatch),
        UploadQueueActions: bindActionCreators(uploadQueueActions, dispatch),
        SourcesActions: bindActionCreators(sourcesActions, dispatch),
        NotificationActions: bindActionCreators(notificationActions, dispatch),
    };
};

UploadFilesPanel = connect(mapStateToProps, mapDispatchToProps)(UploadFilesPanel);
export default withTranslation()(UploadFilesPanel);
